import React, { FC, useCallback, useMemo } from 'react';
import {
  PlatformLinkingConnectionStatus,
  PlatformLinkingProviderEntity,
  PlatformLinkingProvider,
} from '@okletsplay/core';
import { ActionGuard } from 'shared/components/ActionGuard';
import { useDispatch, useSelector } from 'react-redux';
import { modalsReducers } from 'app/store/modal/slice';
import { DialogName, ModalName } from 'app/enums';
import { platformLinkingSelector } from 'modules/platform-linking/store/selectors';
import { ProviderTileVariant } from './provider-tile-variant';
import { ProviderDefaultTile } from './ProviderDefaultTile';
import { ProviderOutlinedTile } from './ProviderOutlinedTile';

interface ProviderTileProps {
  provider: PlatformLinkingProviderEntity;
  variant?: ProviderTileVariant;
  disabled?: boolean;
}

export const ProviderTile: FC<ProviderTileProps> = ({
  provider,
  variant = ProviderTileVariant.Default,
  disabled = false,
}) => {
  const dispatch = useDispatch();

  const isConnected = useMemo(() => {
    return provider?.connectionStatus === PlatformLinkingConnectionStatus.Connected;
  }, [provider?.connectionStatus]);
  const pkceChallenge = useSelector(platformLinkingSelector.pkceChallenge);

  const onClick = useCallback(() => {
    if (disabled) return;

    if (provider?.connectionStatus !== PlatformLinkingConnectionStatus.NotConnected) {
      return;
    }

    switch (provider.platformId) {
      case PlatformLinkingProvider.EvIo:
        dispatch(
          modalsReducers.openModal({ name: ModalName.PlatformLinkingManual, data: provider })
        );
        return;
      case PlatformLinkingProvider.Lichess:
        window.open(`${provider?.authorizeRedirectURI}&code_challenge=${pkceChallenge}`, '_blank');
        break;
      default:
        window.open(provider?.authorizeRedirectURI, '_blank');
        break;
    }

    dispatch(modalsReducers.openModal({ name: ModalName.PlatformLinking, data: provider }));
  }, [disabled, dispatch, pkceChallenge, provider]);

  const onUnlink = useCallback(() => {
    if (disabled) return;

    dispatch(
      modalsReducers.openModal({ name: DialogName.UnlinkPlatform, data: provider?.platformId })
    );
  }, [disabled, dispatch, provider?.platformId]);

  const content = useMemo(() => {
    switch (variant) {
      case ProviderTileVariant.Default:
        return (
          <ProviderDefaultTile
            provider={provider}
            disabled={disabled}
            isConnected={isConnected}
            onClick={onClick}
            onUnlinkClick={onUnlink}
          />
        );
      case ProviderTileVariant.Outlined:
        return (
          <ProviderOutlinedTile
            provider={provider}
            disabled={disabled}
            isConnected={isConnected}
            onClick={onClick}
            onUnlinkClick={onUnlink}
          />
        );
    }
  }, [disabled, isConnected, onClick, onUnlink, provider, variant]);

  return <ActionGuard>{content}</ActionGuard>;
};
